import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import { users, messages } from '../data/mockData';

const MessagingContainer = styled.div`
  display: grid;
  grid-template-columns: 300px 1fr;
  gap: ${({ theme }) => theme.spacing.md};
  height: calc(100vh - 200px);
  min-height: 600px;
  background-color: ${({ theme }) => theme.colors.background};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.md};
  overflow: hidden;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: 1fr;
  }
`;

const ChatsList = styled.div`
  border-right: 1px solid ${({ theme }) => theme.colors.border};
  overflow-y: auto;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    display: ${({ isVisible }) => (isVisible ? 'block' : 'none')};
    border-right: none;
  }
`;

const ChatsHeader = styled.div`
  padding: ${({ theme }) => theme.spacing.md};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  position: sticky;
  top: 0;
  background-color: ${({ theme }) => theme.colors.background};
  z-index: 1;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-family: ${({ theme }) => theme.typography.fontFamily};
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const ChatItem = styled(Link)`
  display: flex;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.md};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  color: ${({ theme }) => theme.colors.text.primary};
  background-color: ${({ active, theme }) => active ? theme.colors.cardBg : 'transparent'};
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.cardBg};
  }
`;

const UserAvatar = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  overflow: hidden;
  margin-right: ${({ theme }) => theme.spacing.md};
  position: relative;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const OnlineIndicator = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.success};
  border: 2px solid ${({ theme }) => theme.colors.background};
  position: absolute;
  bottom: 0;
  right: 0;
`;

const ChatInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const ChatName = styled.div`
  font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const LastMessage = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  color: ${({ theme, unread }) => unread ? theme.colors.text.primary : theme.colors.text.tertiary};
  font-weight: ${({ unread, theme }) => unread ? theme.typography.fontWeights.medium : theme.typography.fontWeights.regular};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ChatTime = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  color: ${({ theme }) => theme.colors.text.tertiary};
  white-space: nowrap;
  margin-left: ${({ theme }) => theme.spacing.sm};
`;

const UnreadBadge = styled.div`
  background-color: ${({ theme }) => theme.colors.primary};
  color: white;
  font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: ${({ theme }) => theme.spacing.sm};
`;

const ChatArea = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    display: ${({ isVisible }) => (isVisible ? 'flex' : 'none')};
  }
`;

const ChatHeader = styled.div`
  display: flex;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.md};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  position: sticky;
  top: 0;
  background-color: ${({ theme }) => theme.colors.background};
  z-index: 1;
`;

const BackButton = styled.button`
  display: none;
  margin-right: ${({ theme }) => theme.spacing.md};
  font-size: ${({ theme }) => theme.typography.fontSizes.lg};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    display: block;
  }
`;

const ChatActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-left: auto;
`;

const ActionButton = styled.button`
  font-size: ${({ theme }) => theme.typography.fontSizes.lg};
  color: ${({ theme }) => theme.colors.text.secondary};
  
  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const MessagesContainer = styled.div`
  flex: 1;
  padding: ${({ theme }) => theme.spacing.md};
  overflow-y: auto;
  display: flex;
  flex-direction: column-reverse;
`;

const MessageGroup = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const MessageDate = styled.div`
  text-align: center;
  margin: ${({ theme }) => theme.spacing.md} 0;
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  color: ${({ theme }) => theme.colors.text.tertiary};
`;

const Message = styled.div`
  display: flex;
  flex-direction: ${({ outgoing }) => outgoing ? 'row-reverse' : 'row'};
  align-items: flex-end;
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const MessageContent = styled.div`
  max-width: 70%;
  padding: ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme, outgoing }) => 
    outgoing 
      ? `${theme.borderRadius.lg} ${theme.borderRadius.lg} 0 ${theme.borderRadius.lg}` 
      : `${theme.borderRadius.lg} ${theme.borderRadius.lg} ${theme.borderRadius.lg} 0`
  };
  background-color: ${({ theme, outgoing }) => 
    outgoing ? theme.colors.primary : theme.colors.cardBg
  };
  color: ${({ theme, outgoing }) => 
    outgoing ? 'white' : theme.colors.text.primary
  };
  position: relative;
`;

const MessageText = styled.p`
  margin: 0;
  word-wrap: break-word;
`;

const MessageTime = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  color: ${({ theme, outgoing }) => 
    outgoing ? 'rgba(255, 255, 255, 0.7)' : theme.colors.text.tertiary
  };
  margin-top: ${({ theme }) => theme.spacing.xs};
  display: block;
  text-align: right;
`;

const MessageMedia = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  
  img {
    max-width: 100%;
    max-height: 300px;
    border-radius: ${({ theme }) => theme.borderRadius.md};
  }
`;

const ComposeArea = styled.div`
  padding: ${({ theme }) => theme.spacing.md};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  background-color: ${({ theme }) => theme.colors.background};
`;

const ComposeForm = styled.form`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const AttachButton = styled.button`
  font-size: ${({ theme }) => theme.typography.fontSizes.lg};
  color: ${({ theme }) => theme.colors.text.secondary};
  
  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const EmojiButton = styled(AttachButton)``;

const MessageInput = styled.input`
  flex: 1;
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-family: ${({ theme }) => theme.typography.fontFamily};
  font-size: ${({ theme }) => theme.typography.fontSizes.md};
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const SendButton = styled.button`
  background: ${({ theme }) => theme.gradients.primary};
  color: white;
  font-size: ${({ theme }) => theme.typography.fontSizes.md};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  transition: all ${({ theme }) => theme.transitions.fast};
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.md};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: ${({ theme }) => theme.spacing.xl};
  text-align: center;
`;

const EmptyStateIcon = styled.div`
  font-size: 4rem;
  margin-bottom: ${({ theme }) => theme.spacing.md};
  color: ${({ theme }) => theme.colors.text.tertiary};
`;

const EmptyStateTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSizes.xl};
  font-weight: ${({ theme }) => theme.typography.fontWeights.semibold};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const EmptyStateText = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSizes.md};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const MessagingPage = () => {
  const { userId } = useParams();
  const [searchQuery, setSearchQuery] = useState('');
  const [messageText, setMessageText] = useState('');
  const [showChatList, setShowChatList] = useState(!userId);
  const [showChatArea, setShowChatArea] = useState(!!userId);

  // Get unique conversations from messages
  const conversations = users.filter(user => user.id !== 1); // Assuming current user is id 1

  // Filter conversations based on search query
  const filteredConversations = conversations.filter(user =>
    user.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Find the active conversation
  const activeUser = userId ? users.find(user => user.id === parseInt(userId)) : null;

  // Get messages for the active conversation
  const conversationMessages = userId
    ? messages.filter(
        msg =>
          (msg.sender.id === 1 && msg.receiver.id === parseInt(userId)) ||
          (msg.sender.id === parseInt(userId) && msg.receiver.id === 1)
      ).sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
    : [];

  const handleSendMessage = (e) => {
    e.preventDefault();
    if (messageText.trim()) {
      // In a real app, this would send the message to the server
      alert(`Message sent to ${activeUser.name}: ${messageText}`);
      setMessageText('');
    }
  };

  const handleChatSelect = () => {
    setShowChatList(false);
    setShowChatArea(true);
  };

  const handleBackToList = () => {
    setShowChatList(true);
    setShowChatArea(false);
  };

  // Format timestamp for display
  const formatMessageTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Format date for message groups
  const formatMessageDate = (timestamp) => {
    const date = new Date(timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString();
    }
  };

  return (
    <MessagingContainer>
      <ChatsList isVisible={showChatList}>
        <ChatsHeader>
          <SearchInput
            type="text"
            placeholder="Search conversations..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </ChatsHeader>

        {filteredConversations.map(user => {
          // Find the last message in this conversation
          const lastMessage = messages
            .filter(msg =>
              (msg.sender.id === 1 && msg.receiver.id === user.id) ||
              (msg.sender.id === user.id && msg.receiver.id === 1)
            )
            .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))[0];

          // Count unread messages
          const unreadCount = messages.filter(msg =>
            msg.sender.id === user.id &&
            msg.receiver.id === 1 &&
            !msg.read
          ).length;

          return (
            <ChatItem
              key={user.id}
              to={`/messages/${user.id}`}
              active={userId === user.id.toString()}
              onClick={handleChatSelect}
            >
              <UserAvatar>
                <img src={user.avatar} alt={user.name} />
                <OnlineIndicator />
              </UserAvatar>
              <ChatInfo>
                <ChatName>{user.name}</ChatName>
                {lastMessage && (
                  <LastMessage unread={!lastMessage.read && lastMessage.sender.id !== 1}>
                    {lastMessage.sender.id === 1 ? 'You: ' : ''}{lastMessage.content}
                  </LastMessage>
                )}
              </ChatInfo>
              {lastMessage && (
                <ChatTime>{formatMessageTime(lastMessage.timestamp)}</ChatTime>
              )}
              {unreadCount > 0 && (
                <UnreadBadge>{unreadCount}</UnreadBadge>
              )}
            </ChatItem>
          );
        })}
      </ChatsList>

      <ChatArea isVisible={showChatArea}>
        {activeUser ? (
          <>
            <ChatHeader>
              <BackButton onClick={handleBackToList}>←</BackButton>
              <UserAvatar>
                <img src={activeUser.avatar} alt={activeUser.name} />
                <OnlineIndicator />
              </UserAvatar>
              <ChatInfo>
                <ChatName>{activeUser.name}</ChatName>
                <LastMessage>Online</LastMessage>
              </ChatInfo>
              <ChatActions>
                <ActionButton>📞</ActionButton>
                <ActionButton>📹</ActionButton>
                <ActionButton>ℹ️</ActionButton>
              </ChatActions>
            </ChatHeader>

            <MessagesContainer>
              {conversationMessages.length > 0 ? (
                conversationMessages.map((message, index) => {
                  const isOutgoing = message.sender.id === 1;
                  const showDate = index === conversationMessages.length - 1 ||
                    formatMessageDate(message.timestamp) !== formatMessageDate(conversationMessages[index + 1].timestamp);

                  return (
                    <React.Fragment key={message.id}>
                      {showDate && (
                        <MessageDate>{formatMessageDate(message.timestamp)}</MessageDate>
                      )}
                      <Message outgoing={isOutgoing}>
                        {!isOutgoing && (
                          <UserAvatar style={{ width: '32px', height: '32px', marginRight: '8px' }}>
                            <img src={message.sender.avatar} alt={message.sender.name} />
                          </UserAvatar>
                        )}
                        <MessageContent outgoing={isOutgoing}>
                          {message.media && (
                            <MessageMedia>
                              <img src={message.media} alt="Shared media" />
                            </MessageMedia>
                          )}
                          <MessageText>{message.content}</MessageText>
                          <MessageTime outgoing={isOutgoing}>{formatMessageTime(message.timestamp)}</MessageTime>
                        </MessageContent>
                      </Message>
                    </React.Fragment>
                  );
                })
              ) : (
                <EmptyState>
                  <EmptyStateIcon>💬</EmptyStateIcon>
                  <EmptyStateTitle>No messages yet</EmptyStateTitle>
                  <EmptyStateText>Send a message to start the conversation!</EmptyStateText>
                </EmptyState>
              )}
            </MessagesContainer>

            <ComposeArea>
              <ComposeForm onSubmit={handleSendMessage}>
                <AttachButton type="button">📎</AttachButton>
                <MessageInput
                  type="text"
                  placeholder="Type a message..."
                  value={messageText}
                  onChange={(e) => setMessageText(e.target.value)}
                />
                <EmojiButton type="button">😊</EmojiButton>
                <SendButton type="submit" disabled={!messageText.trim()}>Send</SendButton>
              </ComposeForm>
            </ComposeArea>
          </>
        ) : (
          <EmptyState>
            <EmptyStateIcon>✉️</EmptyStateIcon>
            <EmptyStateTitle>Select a conversation</EmptyStateTitle>
            <EmptyStateText>Choose a conversation from the list to start messaging</EmptyStateText>
          </EmptyState>
        )}
      </ChatArea>
    </MessagingContainer>
  );
};

export default MessagingPage;
